import { createClient } from '@supabase/supabase-js'
import type { LinkRow } from './linksApi'

/**
 * Supabase access layer.
 *
 * There is no Supabase Auth here. The only credential is the personal-link
 * token, passed explicitly to each RPC. The anon key in the bundle grants
 * nothing on its own: every table has RLS enabled with zero policies, and only
 * the four RPCs below are executable by `anon`. Verified end-to-end 2026-08-19,
 * including that a direct read of `employees` returns an empty array.
 */

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !anonKey) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Copy .env.example to .env.local.',
  )
}

export const supabase = createClient(url, anonKey, {
  auth: { persistSession: false, autoRefreshToken: false },
})

// --- Types --------------------------------------------------------------------

export type Status = 'Not Started' | 'In Progress' | 'Completed'
export type Priority = 'High' | 'Medium' | 'Low'

export const STATUSES: readonly Status[] = ['Not Started', 'In Progress', 'Completed']

/**
 * How each status reads on a button, and which colour its "selected" state
 * takes (see `.seg__btn--todo/--working/--done` in styles.css). One table so the
 * task card and the admin card cannot drift apart.
 */
export const STATUS_LABEL: Record<Status, string> = {
  'Not Started': 'To do',
  'In Progress': 'Working',
  Completed: 'Done',
}
export const STATUS_TONE: Record<Status, 'todo' | 'working' | 'done'> = {
  'Not Started': 'todo',
  'In Progress': 'working',
  Completed: 'done',
}

export interface Me {
  employee_id: string
  full_name: string
  is_manager: boolean
  is_admin: boolean
  is_super_admin: boolean
}

export interface TaskRow {
  assignment_id: string
  task_id: string
  title: string
  priority: Priority
  due_date: string // YYYY-MM-DD, already shop-local
  task_notes: string | null
  status: Status
  note: string | null
  completed_at: string | null
  /**
   * Days from today to the due date, computed by Postgres in America/New_York.
   * Negative = overdue, 0 = today.
   *
   * ALWAYS use this for urgency. Never recompute it in the browser: a phone set
   * to another timezone would disagree with the shop, and that exact class of
   * bug bit the PTO project twice.
   */
  days_until: number
  /** URLs attached to this task. Empty array when there are none. */
  links: LinkRow[]
}

/** Raised when the link is missing, unrecognised, or has been revoked. */
export class InvalidLinkError extends Error {
  constructor() {
    super('This link is not recognised. Ask Scott for a new one.')
    this.name = 'InvalidLinkError'
  }
}

interface Postgrestish {
  code?: string
  message?: string
}

function translate(error: Postgrestish): Error {
  // Codes raised deliberately by the RPCs — see 0001_init.sql.
  if (error.code === '28000') return new InvalidLinkError()
  if (error.code === '42501') {
    return new Error('That task is not assigned to you, so it was not changed.')
  }
  if (error.code === '22023') return new Error('That status is not allowed.')
  return new Error(error.message || 'Something went wrong. Try again.')
}

// --- RPCs ---------------------------------------------------------------------

export interface SignInName {
  employee_id: string
  full_name: string
  /** True for admins, who must also enter a PIN. */
  requires_pin: boolean
  /** False if an admin has no PIN set yet — they cannot sign in until Scott sets one. */
  pin_ready: boolean
}

export interface SignInResult {
  ok: boolean
  token: string | null
  message: string | null
}

/** The sign-in picker. Names only — no emails, no tokens, no PIN material. */
export async function listSignInNames(): Promise<SignInName[]> {
  const { data, error } = await supabase.rpc('list_signin_names')
  if (error) throw translate(error)
  return (data as SignInName[] | null) ?? []
}

/**
 * Exchange a chosen name (plus a PIN, for admins) for that person's access
 * token — the very same token the personal links carry, so everything
 * downstream is unchanged.
 *
 * Returns a result object rather than throwing on a wrong PIN: the function
 * records failed attempts, and a thrown error would roll that write back.
 */
export async function signIn(employeeId: string, pin?: string): Promise<SignInResult> {
  const { data, error } = await supabase.rpc('sign_in', {
    p_employee_id: employeeId,
    p_pin: pin ?? null,
  })
  if (error) throw translate(error)

  const row = (data as SignInResult[] | null)?.[0]
  return row ?? { ok: false, token: null, message: 'Could not sign in. Try again.' }
}

export async function getMe(token: string): Promise<Me> {
  const { data, error } = await supabase.rpc('get_me', { p_token: token })
  if (error) throw translate(error)

  const row = (data as Me[] | null)?.[0]
  // A valid token always returns a row, so an empty result means the token was
  // not accepted rather than "this person has no record".
  if (!row) throw new InvalidLinkError()
  return row
}

export async function getMyTasks(token: string): Promise<TaskRow[]> {
  const { data, error } = await supabase.rpc('get_my_tasks', { p_token: token })
  if (error) throw translate(error)
  return (data as TaskRow[] | null) ?? []
}

/**
 * Every open task in the shop, for the meeting's To-Dos section.
 *
 * The admin task shape (adminApi.ts) MINUS each assignee's private note —
 * "just for you and Scott" stays that way. Everyone can call it: Scott's
 * decision that the meeting reviews every open to-do together, the same
 * full-visibility rule as Issues and the scorecard. Archived and all-done
 * tasks are left out server-side.
 */
export interface OpenTaskAssignee {
  assignment_id: string
  employee_id: string
  full_name: string
  status: Status
  completed_at: string | null
}

export interface OpenTask {
  task_id: string
  title: string
  priority: Priority
  due_date: string
  notes: string | null
  archived: boolean
  days_until: number
  done_count: number
  total_count: number
  assignees: OpenTaskAssignee[]
  issue_id: string | null
  issue_title: string | null
  links: LinkRow[]
  headline_id: string | null
  headline_body: string | null
  /** Live (unarchived) issues raised from this task (0019). */
  raised_issue_count: number
}

export async function listOpenTasks(token: string): Promise<OpenTask[]> {
  const { data, error } = await supabase.rpc('list_open_tasks', { p_token: token })
  if (error) throw translate(error)
  return (data as OpenTask[] | null) ?? []
}

export async function setMyStatus(
  token: string,
  assignmentId: string,
  status: Status,
): Promise<void> {
  const { error } = await supabase.rpc('set_my_status', {
    p_token: token,
    p_assignment_id: assignmentId,
    p_status: status,
  })
  if (error) throw translate(error)
}

/**
 * Change your own PIN. Requires a valid token, so only someone already signed
 * in can do it. Deliberately does not ask for the old PIN — they just signed in
 * with it, and the realistic risk here is a typo locking someone out, which the
 * confirm field in the UI guards against.
 */
export async function setMyPin(token: string, newPin: string): Promise<void> {
  const { error } = await supabase.rpc('set_my_pin', {
    p_token: token,
    p_new_pin: newPin,
  })
  if (error) throw translate(error)
}

/**
 * Edit the task's shared brief — what everyone on the task sees.
 *
 * NOT the same as `setMyNote`, which is this person's own note on their own
 * assignment. Allowed for anyone the task is assigned to, plus admins.
 *
 * Deliberately notes-only: title, due date and priority stay admin-only, because
 * someone quietly moving their own due date would make the overdue buckets and
 * the morning reminder meaningless.
 */
export async function updateTaskNotes(
  token: string,
  taskId: string,
  notes: string,
): Promise<void> {
  const { error } = await supabase.rpc('update_task_notes', {
    p_token: token,
    p_task_id: taskId,
    p_notes: notes,
  })
  if (error) throw translate(error)
}

export async function setMyNote(
  token: string,
  assignmentId: string,
  note: string,
): Promise<void> {
  const { error } = await supabase.rpc('set_my_note', {
    p_token: token,
    p_assignment_id: assignmentId,
    p_note: note,
  })
  if (error) throw translate(error)
}
